import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Snackbar, Button, Stack, Typography } from '@mui/material';
import { Storage } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import MasterCatalogSidebar, { MasterCatalogDrawer, MasterCatalogMobileTrigger } from '../../components/masters/MasterCatalogPanel';
import MasterTypeHeader from '../../components/masters/MasterTypeHeader';
import MasterRecordTable from '../../components/masters/MasterRecordTable';
import MasterFormDrawer, { MasterFormState } from '../../components/masters/MasterFormDrawer';
import CategoryChildrenDrawer from '../../components/masters/CategoryChildrenDrawer';
import { selectEnabledModules } from '../../store/slices/tenantSlice';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { filterSetupPhases, HIERARCHY_PARENT_TYPE, type MasterSetupPhase } from '../../constants/masterSetupFlow';
import {
  getEmbeddedExternalCatalogGroup,
  embeddedExternalMasterMeta,
  isEmbeddedExternalMaster,
  isEmbeddedExternalType,
} from '../../constants/commonExternalMasters';
import { filterMasterCatalog } from '../../utils/serviceMasterCatalog';
import PartnerSubscriptionPlanManager from '../../components/partners/PartnerSubscriptionPlanManager';
import { RestaurantsPage } from '../food/FoodPages';
import { LEGACY_PARENT_META_KEY, enrichMetadataFields } from '../../constants/masterRefFields';
import { resolveServiceSetupPhases } from '../../constants/serviceMasterSetupFlow';
import {
  getNextSequentialSortOrder,
  TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES,
  TRANSPORT_STATUS_TOGGLE_TYPES,
  TRANSPORT_HIDE_STATUS_TOGGLE_TYPES,
  canDeleteTripTypeMaster,
  TRANSPORT_HARD_DELETE_TYPES,
  enrichTransportVehicleMetadataFields,
  enrichTransportBrandMetadataFields,
  enrichTransportModelMetadataFields,
  enrichTransportVariantMetadataFields,
  enrichRideAttributeValueMetadataFields,
  enrichRideTagMetadataFields,
  enrichServiceAvailabilityScheduleMetadataFields,
  normalizeServiceAvailabilityDaysMetadata,
  syncServiceAvailabilityDaysForSave,
  resolveTransportMasterImageUrl,
} from '../../constants/transportSubserviceMasters';
import type { ServiceHubType } from '../../constants/moduleFlows';
import {
  ON_DEMAND_MASTER_HELPERS,
  type OnDemandMasterTypeKey,
} from '../../constants/onDemandMasters';
import {
  getMasterCatalog, getMasterSchema, getMasters, createMaster, updateMaster, deleteMaster,
  getMasterOptions, bulkImportMasters, seedBasicMasters,
} from '../../services/platform/masterService';
import MasterImportDialog from '../../components/masters/MasterImportDialog';
import { downloadMasterCsv, masterCsvRowToPayload } from '../../utils/masterCsv';
import type { MasterCsvRow } from '../../utils/masterCsv';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PageShell from '../../components/layout/PageShell';

interface MasterRow {
  _id: string;
  code: string;
  name: string;
  description?: string;
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  parentId?: string | { _id: string; code?: string; name?: string };
  sortOrder?: number;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
}

/** Master types that show an enable/disable switch in the list table */
const MASTER_TYPES_WITH_STATUS_TOGGLE = new Set([
  'countries',
  'transport_countries',
  'transport_cancellation_reasons',
  'languages',
  'service_categories',
  'skills',
  'certificates',
  'experience_levels',
  'pricing_templates',
  'visit_charges',
  'cancellation_reasons',
  ...TRANSPORT_STATUS_TOGGLE_TYPES,
]);


function resolveParentId(
  row: MasterRow,
  parentOptions: Array<{ _id: string; code: string }>,
  type: string,
): string {
  const raw = row.parentId;
  if (raw) {
    if (typeof raw === 'object' && raw !== null && '_id' in raw) {
      return String(raw._id);
    }
    return String(raw);
  }
  const legacyKey = LEGACY_PARENT_META_KEY[type];
  if (legacyKey) {
    const code = row.metadata?.[legacyKey];
    if (typeof code === 'string') {
      const found = parentOptions.find((p) => p.code === code);
      if (found) return found._id;
    }
  }
  return '';
}

const EMPTY_CATALOG: Awaited<ReturnType<typeof getMasterCatalog>> = [];
const EMPTY_PARENT_OPTIONS: Awaited<ReturnType<typeof getMasterOptions>> = [];

const emptyForm = (): MasterFormState => ({
  code: '', name: '', description: '', country: '', state: '', city: '', district: '',
  parentId: '', sortOrder: 0, isActive: true, metadata: {},
});

export default function MasterDataPage({
  embedded = false,
  catalogScope = 'common',
  serviceHub,
  serviceSlug,
  restrictGroupKeys,
  setupPhasesOverride,
  singleTypeMode,
}: {
  embedded?: boolean;
  catalogScope?: 'common' | 'service';
  serviceHub?: string;
  serviceSlug?: string;
  /** When set, only these master groups appear in the catalog sidebar */
  restrictGroupKeys?: string[];
  setupPhasesOverride?: MasterSetupPhase[];
  pageTitle?: string;
  pageSubtitle?: string;
  /** Dedicated page: one master type only — no catalog sidebar or setup guide */
  singleTypeMode?: { groupKey: string; typeKey: string };
} = {}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeType = searchParams.get('type') || '';
  const activeGroup = searchParams.get('group') || '';
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MasterRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MasterRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState<MasterFormState>(emptyForm());
  const [saveError, setSaveError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; failed: { code: string; error: string }[] } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [togglingRowId, setTogglingRowId] = useState<string | null>(null);
  const [partnerPlanAddRequest, setPartnerPlanAddRequest] = useState(0);
  const [restaurantAddRequest, setRestaurantAddRequest] = useState(0);
  const [langToast, setLangToast] = useState<string | null>(null);
  const [categoryChildrenParent, setCategoryChildrenParent] = useState<MasterRow | null>(null);
  const [seedToast, setSeedToast] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const enabledModules = useSelector(selectEnabledModules);

  const seedBasicMutation = useMutation({
    mutationFn: seedBasicMasters,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['masters'] });
      queryClient.invalidateQueries({ queryKey: ['masters-count'] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      queryClient.invalidateQueries({ queryKey: ['master-catalog'] });
      setSeedToast(
        `Basic masters ready for ${result.tenantSlug} (${result.masterCountAfter} records).`,
      );
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message
        || (err as Error)?.message
        || 'Failed to seed basic masters';
      setSeedToast(msg);
    },
  });

  const { data: catalogRaw = EMPTY_CATALOG } = useQuery({
    queryKey: ['master-catalog', catalogScope, serviceHub, serviceSlug],
    queryFn: () => getMasterCatalog({ scope: catalogScope, hub: serviceHub, slug: serviceSlug }),
    staleTime: 5 * 60_000,
  });

  const effectiveRestrictGroupKeys = singleTypeMode
    ? [singleTypeMode.groupKey]
    : restrictGroupKeys;

  const catalog = useMemo(() => filterMasterCatalog(catalogRaw, {
    embedded,
    serviceHub,
    serviceSlug,
    enabledModules,
    isSuperAdmin,
    restrictGroupKeys: effectiveRestrictGroupKeys,
  }), [catalogRaw, enabledModules, isSuperAdmin, effectiveRestrictGroupKeys, embedded, serviceHub, serviceSlug]);

  const setupPhases = useMemo(() => {
    if (singleTypeMode) return [];
    if (setupPhasesOverride?.length) return setupPhasesOverride;
    if (embedded && serviceHub) {
      return resolveServiceSetupPhases(serviceHub as ServiceHubType, serviceSlug, catalog);
    }
    if (!embedded) return filterSetupPhases(enabledModules, isSuperAdmin);
    return [];
  }, [setupPhasesOverride, embedded, serviceHub, serviceSlug, catalog, enabledModules, isSuperAdmin]);

  const resolvedType = useMemo(() => {
    if (singleTypeMode) {
      return { type: singleTypeMode.typeKey, group: singleTypeMode.groupKey };
    }
    if (activeType === 'partner_subscriptions') {
      return { type: 'partner_subscription_plans', group: '_external' };
    }
    if (activeType && activeGroup === '_external') {
      return { type: activeType, group: activeGroup };
    }
    if (activeType) {
      const owningGroup = catalog.find((g) => g.types.some((t) => t.key === activeType));
      if (owningGroup) {
        return { type: activeType, group: owningGroup.key };
      }
    }
    if (embedded && setupPhases.length) {
      const step = setupPhases.flatMap((p) => p.steps).find((s) => !s.externalLink);
      if (step) return { type: step.typeKey, group: step.groupKey };
    }
    const first = catalog[0]?.types[0];
    if (first) return { type: first.key, group: catalog[0].key };
    // Embedded hub masters must not fall back to global geo/countries
    if (embedded) return { type: '', group: '' };
    return { type: 'countries', group: 'geo' };
  }, [catalog, activeType, activeGroup, embedded, setupPhases, singleTypeMode]);

  const currentType = resolvedType.type;
  const currentGroup = resolvedType.group;

  const catalogGroupKeys = useMemo(() => catalog.map((g) => g.key).join('|'), [catalog]);

  useEffect(() => {
    if (singleTypeMode || !catalogGroupKeys) return;
    setOpenGroups(Object.fromEntries(catalogGroupKeys.split('|').map((k) => [k, true])));
  }, [catalogGroupKeys, singleTypeMode]);

  useEffect(() => {
    if (singleTypeMode) return;
    if (!resolvedType.type || !resolvedType.group) return;
    if (resolvedType.type === activeType && resolvedType.group === activeGroup) return;
    setSearchParams({ group: resolvedType.group, type: resolvedType.type }, { replace: true });
    const groupKey = resolvedType.group;
    setOpenGroups((prev) => (prev[groupKey] ? prev : { ...prev, [groupKey]: true }));
  }, [resolvedType.type, resolvedType.group, activeType, activeGroup, setSearchParams, embedded, catalogGroupKeys, singleTypeMode]);

  const isEmbeddedExternal = isEmbeddedExternalType(currentType)
    || isEmbeddedExternalMaster(currentGroup, currentType);
  const embeddedMeta = embeddedExternalMasterMeta(currentType);

  useEffect(() => {
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [currentType, debouncedSearch]);

  const { data: schemaRaw } = useQuery({
    queryKey: ['master-schema', currentType],
    queryFn: () => getMasterSchema(currentType),
    enabled: !isEmbeddedExternal && !!currentType,
    staleTime: 5 * 60_000,
  });
  const schema = useMemo(
    () => {
      if (!schemaRaw) return schemaRaw;
      let metadataFields = enrichMetadataFields(schemaRaw.metadataFields, currentType);
      if (currentType === 'vehicle_types') {
        metadataFields = enrichTransportVehicleMetadataFields(metadataFields);
      }
      if (currentType === 'vehicle_brands') {
        metadataFields = enrichTransportBrandMetadataFields(metadataFields);
      }
      if (currentType === 'vehicle_models') {
        metadataFields = enrichTransportModelMetadataFields(metadataFields);
      }
      if (currentType === 'vehicle_variants') {
        metadataFields = enrichTransportVariantMetadataFields(metadataFields);
      }
      if (currentType === 'ride_attribute_values') {
        metadataFields = enrichRideAttributeValueMetadataFields(metadataFields);
      }
      if (currentType === 'ride_tags') {
        metadataFields = enrichRideTagMetadataFields(metadataFields);
      }
      if (currentType === 'service_availability_schedules') {
        metadataFields = enrichServiceAvailabilityScheduleMetadataFields(metadataFields);
      }
      return { ...schemaRaw, metadataFields };
    },
    [schemaRaw, currentType],
  );
  const rootsOnly = currentType === 'service_categories';
  const { data, isLoading } = useQuery({
    queryKey: ['masters', currentType, debouncedSearch, paginationModel.page, paginationModel.pageSize, rootsOnly],
    queryFn: () => getMasters(currentType, {
      search: debouncedSearch || undefined,
      limit: paginationModel.pageSize,
      page: paginationModel.page + 1,
      rootsOnly: rootsOnly || undefined,
    }),
    enabled: !isEmbeddedExternal && !!currentType,
    staleTime: 30_000,
  });

  const totalRows = data?.meta?.total ?? 0;
  const { data: mastersForDuplicateCheck } = useQuery({
    queryKey: ['masters', currentType, 'duplicate-check'],
    queryFn: () => getMasters(currentType, { limit: 10000, page: 1, rootsOnly: rootsOnly || undefined }),
    enabled: dialogOpen && !isEmbeddedExternal && !!currentType,
    staleTime: 0,
  });
  const existingMasterRecords = useMemo(
    () => ((mastersForDuplicateCheck?.data ?? []) as MasterRow[]).map((row) => {
      const meta = row.metadata || {};
      const rawParentId = row.parentId;
      const parentId = typeof rawParentId === 'object' && rawParentId?._id
        ? rawParentId._id
        : typeof rawParentId === 'string'
          ? rawParentId
          : undefined;
      return {
        _id: row._id,
        code: row.code,
        sortOrder: row.sortOrder ?? (Number(meta.sortOrder) || 0),
        parentId,
        attributeCode: meta.attributeCode != null ? String(meta.attributeCode) : undefined,
      };
    }),
    [mastersForDuplicateCheck],
  );
  const lockedSortOrder = useMemo(() => {
    if (!TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)) return null;
    return getNextSequentialSortOrder(totalRows);
  }, [currentType, totalRows]);
  const parentMasterType = HIERARCHY_PARENT_TYPE[currentType];
  const { data: parentOptionsData } = useQuery({
    queryKey: ['master-parent-options', currentType, parentMasterType],
    queryFn: () => getMasterOptions(parentMasterType!, { parentType: parentMasterType }),
    enabled: !!parentMasterType && !!schema?.supportsHierarchy,
  });
  const parentOptions = useMemo(() => {
    const all = parentOptionsData ?? EMPTY_PARENT_OPTIONS;
    // Primary→sub: only offer root (no parent) categories as parents
    if (currentType === 'service_categories' && parentMasterType === 'service_categories') {
      return all.filter((p) => {
        const row = p as { parentId?: string | null };
        return !row.parentId;
      });
    }
    return all;
  }, [parentOptionsData, currentType, parentMasterType]);

  const activeTypeDef = useMemo(() => {
    if (isEmbeddedExternal && embeddedMeta) {
      const externalGroup = getEmbeddedExternalCatalogGroup({
        embedded: !!embedded,
        serviceHub,
        serviceSlug,
        enabledModules,
        isSuperAdmin,
      });
      return {
        group: catalog.find((g) => g.key === '_external') || externalGroup || catalog[0],
        type: {
          key: currentType,
          label: embeddedMeta.label,
          description: embeddedMeta.description,
          supportsHierarchy: false,
          geoFields: [],
          hasMetadata: false,
        },
      };
    }
    for (const g of catalog) {
      const t = g.types.find((x) => x.key === currentType);
      if (t) return { group: g, type: t };
    }
    return null;
  }, [catalog, currentType, embeddedMeta, isEmbeddedExternal, embedded, serviceHub, serviceSlug, enabledModules, isSuperAdmin]);

  useEffect(() => {
    if (!dialogOpen) return;
    if (editing) {
      const meta: Record<string, string | number | boolean> = {};
      Object.entries(editing.metadata || {}).forEach(([k, v]) => {
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') meta[k] = v;
      });
      const isActive = editing.isActive !== false;
      if (schema?.metadataFields?.some((f) => f.key === 'status') && meta.status === undefined) {
        meta.status = isActive ? 'active' : 'inactive';
      }
      if (currentType === 'vehicle_types' && !meta.serviceTypes && meta.serviceType) {
        meta.serviceTypes = String(meta.serviceType);
        delete meta.serviceType;
      }
      if (currentType === 'service_availability_schedules') {
        Object.assign(meta, normalizeServiceAvailabilityDaysMetadata(meta));
      }
      setForm({
        code: editing.code,
        name: editing.name,
        description: editing.description || '',
        country: editing.country || '',
        state: editing.state || '',
        city: editing.city || '',
        district: editing.district || '',
        parentId: resolveParentId(editing, parentOptions, currentType),
        sortOrder: editing.sortOrder || Number(meta.sortOrder) || 0,
        isActive,
        metadata: meta,
      });
    } else {
      const base = emptyForm();
      if (currentType === 'languages') {
        base.metadata = { autoTranslate: true, rtl: false };
      }
      if (schema?.metadataFields?.some((f) => f.key === 'status')) {
        base.metadata = { ...base.metadata, status: 'active' };
      }
      if (TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)) {
        base.sortOrder = getNextSequentialSortOrder(totalRows);
      }
      setForm(base);
    }
  }, [editing, dialogOpen, parentOptions, currentType, schema, totalRows]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const metadata = { ...form.metadata };
      if (currentType === 'languages' && metadata.autoTranslate === undefined) {
        metadata.autoTranslate = true;
      }
      if (currentType === 'service_availability_schedules') {
        Object.assign(metadata, syncServiceAvailabilityDaysForSave(metadata));
      }
      let parentId = form.parentId;
      const legacyKey = LEGACY_PARENT_META_KEY[currentType];
      if (legacyKey) {
        const metaCode = String(metadata[legacyKey] || '');
        if (metaCode && !parentId) {
          const parent = parentOptions.find((p) => p.code === metaCode);
          if (parent) parentId = parent._id;
        }
      }
      if (parentId) {
        const parent = parentOptions.find((p) => p._id === parentId);
        if (parent && legacyKey) {
          metadata[legacyKey] = parent.code;
        }
      }
      // Keep metadata.status in sync with record active flag
      if (
        schema?.metadataFields?.some((f) => f.key === 'status')
        || TRANSPORT_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
      ) {
        metadata.status = form.isActive === false ? 'inactive' : 'active';
      }
      // Prefer top-level sortOrder; mirror into metadata when schema exposes it
      if (schema?.metadataFields?.some((f) => f.key === 'sortOrder')) {
        metadata.sortOrder = form.sortOrder;
      }
      const payload: Record<string, unknown> = {
        code: form.code,
        name: form.name,
        description: form.description || undefined,
        country: form.country || undefined,
        state: form.state || undefined,
        city: form.city || undefined,
        district: form.district || undefined,
        sortOrder: form.sortOrder,
        isActive: form.isActive !== false,
        metadata,
      };
      if (parentId) payload.parentId = parentId;
      else if (currentType === 'service_categories') payload.parentId = null;
      if (editing) return updateMaster(editing._id, payload);
      return createMaster(currentType, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      queryClient.invalidateQueries({ queryKey: ['masters', currentType, 'duplicate-check'] });
      if (currentType === 'languages') {
        queryClient.invalidateQueries({ queryKey: ['locale-languages'] });
        queryClient.invalidateQueries({ queryKey: ['translations'] });
        setLangToast(
          editing
            ? 'Language saved. Auto-translate will fill / refresh translation keys from English.'
            : 'Language added. Auto-translate started — all English admin + mobile keys will be converted.',
        );
      }
      setDialogOpen(false);
      setEditing(null);
      setSaveError(null);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || '';
      if (/duplicate|E11000|already exists|already used/i.test(msg)) {
        setSaveError(msg || 'This value is already used by another record');
        return;
      }
      setSaveError(msg || 'Could not save record');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMaster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      if (TRANSPORT_HARD_DELETE_TYPES.has(currentType)) {
        queryClient.invalidateQueries({ queryKey: ['masters', currentType, 'duplicate-check'] });
      }
      setDeleteTarget(null);
      setDeleteError(null);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setDeleteError(msg || 'Could not delete record');
    },
  });

  const canDeleteRow = useCallback((row: MasterRow) => {
    if (currentType === 'trip_types') return canDeleteTripTypeMaster(row);
    return true;
  }, [currentType]);

  const requestDelete = useCallback((row: MasterRow) => {
    setDeleteError(null);
    setDeleteTarget(row);
  }, []);

  const supportsStatusToggle = MASTER_TYPES_WITH_STATUS_TOGGLE.has(currentType)
    || TRANSPORT_STATUS_TOGGLE_TYPES.has(currentType);

  const toggleActiveMutation = useMutation({
    mutationFn: ({ row, active }: { row: MasterRow; active: boolean }) => {
      const payload: Record<string, unknown> = {
        code: row.code,
        name: row.name,
        description: row.description,
        isActive: active,
      };
      if (currentType === 'transport_countries' || currentType === 'countries') {
        payload.metadata = { ...(row.metadata || {}), status: active };
      }
      if (TRANSPORT_STATUS_TOGGLE_TYPES.has(currentType)) {
        payload.metadata = {
          ...(row.metadata || {}),
          status: active ? 'active' : 'inactive',
        };
      }
      return updateMaster(row._id, payload);
    },
    onMutate: ({ row }) => setTogglingRowId(row._id),
    onSettled: () => setTogglingRowId(null),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      if (currentType === 'languages') {
        queryClient.invalidateQueries({ queryKey: ['locale-languages'] });
        queryClient.invalidateQueries({ queryKey: ['translations'] });
        window.dispatchEvent(new CustomEvent('locale-languages-changed'));
        if (vars.active) {
          setLangToast(
            `Language "${vars.row.code}" activated. Auto-translate is converting all English keys now.`,
          );
        } else {
          setLangToast(`Language "${vars.row.code}" deactivated. It is hidden from the panel language menu.`);
        }
      }
    },
    onError: (err: unknown) => {
      if (currentType !== 'languages') return;
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Could not update language status';
      setLangToast(msg);
    },
  });

  const handleToggleActive = useCallback(
    (row: MasterRow, active: boolean) => {
      toggleActiveMutation.mutate({ row, active });
    },
    [toggleActiveMutation],
  );

  const importMutation = useMutation({
    mutationFn: (rows: MasterCsvRow[]) => bulkImportMasters(
      currentType,
      rows.map((r) => masterCsvRowToPayload(r)),
    ),
    onSuccess: (result) => {
      setImportResult(result);
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
    },
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getMasters(currentType, {
        search: debouncedSearch || undefined,
        limit: 10000,
        page: 1,
      });
      const exportRows = (res.data || []) as MasterRow[];
      downloadMasterCsv(exportRows as unknown as Record<string, unknown>[], currentType, schema);
    } finally {
      setExporting(false);
    }
  };

  const handleImportOpen = () => {
    setImportResult(null);
    setImportOpen(true);
  };

  const handleImport = (rows: MasterCsvRow[]) => {
    importMutation.mutate(rows);
  };

  const selectType = useCallback((groupKey: string, typeKey: string) => {
    setSearchParams({ group: groupKey, type: typeKey });
    setCatalogOpen(false);
    setOpenGroups((prev) => ({ ...prev, [groupKey]: true }));
  }, [setSearchParams]);

  const toggleGroup = useCallback((key: string) => {
    setOpenGroups((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const totalTypes = catalog.reduce((n, g) => n + g.types.length, 0);
  const rows = (data?.data || []) as MasterRow[];

  const catalogProps = {
    catalog,
    currentGroup,
    currentType,
    openGroups,
    onToggleGroup: toggleGroup,
    onSelectType: selectType,
    totalTypes,
    accordionMode: false,
  };

  const openCreate = () => {
    if (isEmbeddedExternal && currentType === 'partner_subscription_plans') {
      setPartnerPlanAddRequest((n) => n + 1);
      return;
    }
    if (isEmbeddedExternal && currentType === 'restaurants') {
      setRestaurantAddRequest((n) => n + 1);
      return;
    }
    setEditing(null);
    setSaveError(null);
    setDialogOpen(true);
  };
  const openEdit = (row: MasterRow) => {
    setEditing(row);
    setSaveError(null);
    setDialogOpen(true);
  };

  const typeHelper = ON_DEMAND_MASTER_HELPERS[currentType as OnDemandMasterTypeKey];
  const headerDescription = typeHelper
    || embeddedMeta?.description
    || schema?.description
    || activeTypeDef?.type.description;

  const content = (
    <Box sx={{ minHeight: embedded ? undefined : '100%', minWidth: 0 }}>
      {!singleTypeMode && !embedded && isSuperAdmin && (
        <Alert
          severity="info"
          sx={{ mb: 1.5, borderRadius: 2 }}
          action={(
            <Button
              color="inherit"
              size="small"
              startIcon={<Storage />}
              disabled={seedBasicMutation.isPending}
              onClick={() => seedBasicMutation.mutate()}
            >
              {seedBasicMutation.isPending ? 'Seeding…' : 'Seed basic masters'}
            </Button>
          )}
        >
          <Stack spacing={0.25}>
            <Typography variant="body2" fontWeight={700}>Platform basics</Typography>
            <Typography variant="caption" color="text.secondary">
              Load countries, geo, transport/porter/provider catalog masters for the current tenant (same as demo tenant bootstrap). Safe to re-run — upserts existing codes.
            </Typography>
          </Stack>
        </Alert>
      )}

      {!singleTypeMode && !embedded && (
        <MasterCatalogDrawer open={catalogOpen} onClose={() => setCatalogOpen(false)} {...catalogProps} />
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          minWidth: 0,
          minHeight: embedded ? undefined : 'calc(100vh - 96px)',
          borderRadius: embedded ? 0 : 2.5,
          border: embedded ? 0 : 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {!singleTypeMode && !embedded && (
          <MasterCatalogSidebar {...catalogProps} />
        )}

        <Box
          flex={1}
          minWidth={0}
          width="100%"
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <MasterTypeHeader
            title={embeddedMeta?.label || schema?.label || currentType.replace(/_/g, ' ')}
            description={embedded ? undefined : headerDescription}
            groupLabel={embedded || singleTypeMode ? undefined : activeTypeDef?.group.label}
            recordCount={isEmbeddedExternal ? undefined : rows.length}
            totalCount={isEmbeddedExternal ? undefined : totalRows}
            search={isEmbeddedExternal ? undefined : search}
            onSearchChange={isEmbeddedExternal ? undefined : setSearch}
            onAdd={openCreate}
            addLabel={
              isEmbeddedExternal && currentType === 'partner_subscription_plans'
                ? 'Add plan'
                : isEmbeddedExternal && currentType === 'restaurants'
                  ? 'Add Restaurant'
                  : currentType === 'service_categories'
                    ? 'Add primary'
                    : 'Add'
            }
            onExport={isEmbeddedExternal ? undefined : handleExport}
            onImport={isEmbeddedExternal ? undefined : handleImportOpen}
            exportLoading={exporting}
            catalogTrigger={singleTypeMode || embedded ? undefined : <MasterCatalogMobileTrigger onClick={() => setCatalogOpen(true)} />}
          />

          <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1.5, md: 2 } }}>
            {isEmbeddedExternal && currentType === 'partner_subscription_plans' ? (
              <PartnerSubscriptionPlanManager
                showHeader={false}
                addPlanRequestId={partnerPlanAddRequest}
              />
            ) : isEmbeddedExternal && currentType === 'restaurants' ? (
              <RestaurantsPage showHeader={false} addRequestId={restaurantAddRequest} />
            ) : (
              <MasterRecordTable
                rows={rows}
                schema={schema}
                masterTypeKey={currentType}
                loading={isLoading}
                totalCount={totalRows}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                onPageChange={(page) => setPaginationModel((p) => ({ ...p, page }))}
                onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
                onEdit={openEdit}
                onDelete={requestDelete}
                canDelete={canDeleteRow}
                onView={currentType === 'service_categories' ? setCategoryChildrenParent : undefined}
                viewTooltip="View sub-categories"
                onToggleActive={supportsStatusToggle ? handleToggleActive : undefined}
                togglingRowId={supportsStatusToggle ? togglingRowId : null}
              />
            )}
          </Box>
        </Box>
      </Box>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete record?"
        message={
          currentType === 'trip_types'
            ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the trip type from the list.`
            : currentType === 'vehicle_types'
              ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the vehicle type from the list.`
              : currentType === 'vehicle_brands'
                ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the brand from the list.`
                : currentType === 'vehicle_models'
                  ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the vehicle model from the list.`
                  : currentType === 'vehicle_variants'
                    ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the vehicle variant from the list.`
                    : currentType === 'ride_attributes'
                      ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the ride attribute from the list.`
                      : currentType === 'ride_attribute_values'
                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the ride attribute value from the list.`
                        : currentType === 'ride_tags'
                          ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the ride tag from the list.`
                          : currentType === 'service_availability_schedules'
                            ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the availability schedule from the list.`
                            : `Remove "${deleteTarget?.name}" (${deleteTarget?.code})? This cannot be undone.`
        }
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
      />

      <Snackbar
        open={!!deleteError}
        autoHideDuration={7000}
        onClose={() => setDeleteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setDeleteError(null)} variant="filled" sx={{ width: '100%' }}>
          {deleteError}
        </Alert>
      </Snackbar>

      <CategoryChildrenDrawer
        open={!!categoryChildrenParent}
        parent={categoryChildrenParent}
        onClose={() => setCategoryChildrenParent(null)}
      />

      <MasterFormDrawer
        open={dialogOpen}
        editing={!!editing}
        schema={schema}
        currentType={currentType}
        form={form}
        onChange={setForm}
        onClose={() => { setDialogOpen(false); setSaveError(null); }}
        onSubmit={() => saveMutation.mutate()}
        loading={saveMutation.isPending}
        parentMasterType={parentMasterType}
        parentOptions={parentOptions}
        lockedSortOrder={!editing ? lockedSortOrder : null}
        existingRecords={existingMasterRecords}
        editingId={editing?._id}
        saveError={saveError}
      />

      <MasterImportDialog
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportResult(null); }}
        masterType={currentType}
        masterLabel={schema?.label || currentType}
        schema={schema}
        loading={importMutation.isPending}
        onImport={handleImport}
        result={importResult}
      />

      <Snackbar
        open={!!langToast}
        autoHideDuration={7000}
        onClose={() => setLangToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setLangToast(null)} variant="filled" sx={{ width: '100%' }}>
          {langToast}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!seedToast}
        autoHideDuration={6000}
        onClose={() => setSeedToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={seedToast?.startsWith('Failed') || seedToast?.includes('Failed') ? 'error' : 'success'}
          onClose={() => setSeedToast(null)}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {seedToast}
        </Alert>
      </Snackbar>
    </Box>
  );

  if (embedded) return content;
  return <PageShell maxWidth={false}>{content}</PageShell>;
}
